import { analytics } from '@/features/analytics/events'
import { useNectarProject, useNectarTask } from '@/features/nectar/api'
import type { AudioTranscriptionTaskDetail, AudioTranscriptionTypeConfig } from '@/features/nectar/api/models'
import { NectarAudioPlayerAdvanced } from '@/features/nectar/components/shared/NectarAudioPlayerAdvanced'
import { useSubmitAudioTranscription } from '@/features/nectar/hooks/useSubmitAudioTranscription'
import { useTurnstileCaptcha } from '@/features/turnstile'
import { Button, Spinner } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { Lock } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FIELD_COMPONENTS } from '../audio-annotation/fields'
import { validateAnswers } from '../audio-annotation/validateAnswers'
import type { TaskRendererProps } from '../task-types'

export function AudioTranscriptionTask({
  taskId,
  taskIndex,
  projectId,
  captchaEnabled,
  onSubmitted
}: TaskRendererProps) {
  const { t } = useLingui()
  const { data: task, isFetching: isLoadingTask } = useNectarTask(projectId, taskId)
  const { data: project } = useNectarProject(projectId)
  const taskDetail = task as unknown as AudioTranscriptionTaskDetail
  const captcha = useTurnstileCaptcha({ enabled: captchaEnabled, action: 'submission' })
  const { submit, isSubmitting } = useSubmitAudioTranscription({ captcha })

  const fields = useMemo(() => taskDetail?.formSchema?.fields ?? [], [taskDetail?.formSchema?.fields])
  const typeConfig = project?.typeConfig as AudioTranscriptionTypeConfig | null | undefined
  const maxTextChars = typeConfig?.limits?.maxTextChars

  const [transcription, setTranscription] = useState('')
  const [hasListened, setHasListened] = useState(false)
  const [audioError, setAudioError] = useState(false)
  const [showFields, setShowFields] = useState(false)
  const [answers, setAnswers] = useState<Record<string, unknown>>({})
  const playAttemptRef = useRef(0)
  const textFocusValueRef = useRef<string | null>(null)

  const audioUrl = taskDetail?.audioUrl

  useEffect(() => {
    setTranscription('')
    setHasListened(false)
    setAudioError(false)
    setShowFields(false)
    setAnswers({})
    playAttemptRef.current = 0
    textFocusValueRef.current = null
  }, [audioUrl, fields])

  const setFieldValue = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }))
  }

  const trimmed = transcription.trim()

  const textOptionalMatched = useMemo(() => {
    const cfg = taskDetail?.textOptional
    if (!cfg) return false
    return Object.prototype.hasOwnProperty.call(answers, cfg.fieldKey)
  }, [answers, taskDetail?.textOptional])

  const fieldsValid = useMemo(() => validateAnswers(fields, answers), [fields, answers])

  const baseOk = !audioError && hasListened && !isSubmitting && !(captcha.active && !captcha.token)
  const canSubmit = baseOk && (textOptionalMatched ? fieldsValid : trimmed.length > 0)

  const onSubmit = async () => {
    if (!canSubmit) return
    await submit({
      projectId,
      taskId,
      taskIndex,
      segments: [
        {
          start: 0,
          end: taskDetail?.clipDurationSec ?? 0,
          text: transcription ?? undefined,
          tags: textOptionalMatched ? answers : undefined
        }
      ],
      onSuccess: () => onSubmitted(taskId)
    })
  }

  const submitHint =
    !audioUrl || audioError
      ? t`Audio could not be loaded`
      : !trimmed && !textOptionalMatched
        ? t`Enter the transcription`
        : undefined

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }} className="flex flex-col gap-4">
      {taskDetail?.instruction && (
        <div className="text-body-2 text-gray-80 m-auto block rounded-xl border border-solid border-gray-100 p-4">
          {taskDetail?.instruction}
        </div>
      )}
      <div className="flex flex-col items-stretch gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        <div className="-mx-8 border-b border-solid border-gray-100 px-8 py-4">
          {audioUrl ? (
            <NectarAudioPlayerAdvanced
              audioUrl={audioUrl}
              onPlay={() => {
                setHasListened(true)
                analytics.audioTaskPlayerToggle(projectId, taskId, 'play', ++playAttemptRef.current)
              }}
              onPause={() => analytics.audioTaskPlayerToggle(projectId, taskId, 'pause', ++playAttemptRef.current)}
              onError={() => setAudioError(false)}
            />
          ) : (
            <div className="flex h-10 w-full items-center justify-center">
              {isLoadingTask ? (
                <Spinner size="small" />
              ) : (
                <div className="text-body-3 text-gray-90">
                  <Trans>Audio unavailable</Trans>
                </div>
              )}
            </div>
          )}
        </div>

        {(!audioUrl || audioError) && (
          <div
            className="flex items-center gap-3 rounded-xl border border-solid border-gray-100 bg-gray-100/30 px-4 py-3"
            role="status"
            aria-live="polite"
          >
            <Lock className="text-gray-70 h-4 w-4 shrink-0" />
            <div className="text-body-3 text-gray-70">
              <Trans>Audio could not be loaded — you can’t transcribe this task.</Trans>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-body-2 text-gray-70 font-semibold">
            <Trans>Transcription</Trans>
          </div>
          <textarea
            value={transcription}
            onChange={(e) => setTranscription(maxTextChars ? e.target.value.slice(0, maxTextChars) : e.target.value)}
            onFocus={(e) => {
              textFocusValueRef.current = e.target.value
            }}
            onBlur={(e) => {
              if (textFocusValueRef.current !== null && textFocusValueRef.current !== e.target.value) {
                analytics.audioTaskTextEdited(projectId, taskId, e.target.value.length)
              }
              textFocusValueRef.current = null
            }}
            disabled={!audioUrl || audioError}
            maxLength={maxTextChars}
            placeholder={t`Type what you hear in the audio...`}
            rows={6}
            className="text-body2 min-h-[140px] w-full resize-y rounded-xl border border-solid border-gray-100 bg-transparent p-4 outline-0 transition-colors focus:border-gray-200 disabled:pointer-events-none disabled:opacity-50"
          />
          {maxTextChars && (
            <div className="text-body-3 text-gray-80 self-end tabular-nums">
              {transcription.length}/{maxTextChars}
            </div>
          )}
        </div>

        {fields.length > 0 && (
          <div className={clsx('flex flex-col gap-4')}>
            <button
              type="button"
              onClick={() => {
                if (showFields) {
                  setAnswers({})
                }
                setShowFields((v) => !v)
              }}
              className="text-body-2 text-gray-90 hover:text-green flex w-fit cursor-pointer items-center gap-1 underline transition-colors"
            >
              <Trans>Can't Transcribe</Trans>
            </button>

            {showFields && (
              <div className="flex min-w-0 flex-col gap-6">
                {fields.map((field) => {
                  const Component = FIELD_COMPONENTS.get(field.type)
                  if (!Component) return null
                  return (
                    <Component
                      key={field.key}
                      field={field}
                      value={answers[field.key]}
                      onChange={(value) => setFieldValue(field.key, value)}
                      fieldsClassName="flex-col"
                    />
                  )
                })}
              </div>
            )}
          </div>
        )}

        {captcha.renderWidget('w-full')}

        <Button
          variant="green"
          onClick={onSubmit}
          isDisabled={!canSubmit}
          isLoading={isSubmitting}
          className="w-full bg-transparent! disabled:hover:bg-transparent!"
          title={submitHint}
        >
          {isSubmitting ? <Trans>Sending...</Trans> : <Trans>Submit</Trans>}
        </Button>
      </div>
    </div>
  )
}
